/**
 * DateRangePicker — preset tabs + a "Custom" option that reveals two
 * `<input type="date">` fields (from / to). Used on /reports,
 * /admin/earnings and /admin/dashboard so all three pages share one
 * range-selection UX. The picker is range-only; it doesn't deal with
 * time-of-day (the customer never asks "from 3pm yesterday").
 *
 * Range payload is RFC3339 (UTC), the same shape every reports endpoint
 * already accepts. The picker computes day boundaries in UTC: "from" is
 * 00:00 of the chosen day, "to" is 00:00 of the day AFTER the chosen day
 * so the half-open backend filter [from, to) actually includes the
 * picker's "to" date end-to-end.
 *
 * Validation runs locally (from < to, max 367 days = backend cap). The
 * Apply button stays disabled until the inputs are valid so users can't
 * fire a bad request just to read the same error back.
 */

import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export interface DateRange {
  /** RFC3339 inclusive lower bound. */
  from: string;
  /** RFC3339 exclusive upper bound. */
  to: string;
}

export interface DateRangePreset {
  /** Stable identifier. The literal "custom" is reserved for the picker. */
  key: string;
  /** Translated label shown on the tab. */
  label: string;
  /** Resolved range when this preset is active. */
  range: DateRange;
}

export interface DateRangePickerProps {
  presets: DateRangePreset[];
  /** Active preset key. Use the literal `'custom'` to render the picker. */
  value: string;
  /** Active range — emitted up so the parent owns query keys. */
  range: DateRange;
  onChange(next: { key: string; range: DateRange }): void;
  /** Hides the Custom tab — useful where free-form ranges aren't wanted. */
  disableCustom?: boolean;
}

const MAX_RANGE_DAYS = 367;

/** Convert a YYYY-MM-DD picker value to UTC midnight RFC3339. */
function dateInputToFromIso(value: string): string {
  const d = new Date(`${value}T00:00:00Z`);
  return d.toISOString();
}

/** Convert a YYYY-MM-DD picker value to RFC3339 at start of NEXT day UTC,
 *  so the half-open [from, to) filter includes the chosen end date. */
function dateInputToToIso(value: string): string {
  const d = new Date(`${value}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString();
}

function isoToDateInput(iso: string): string {
  return iso.slice(0, 10);
}

/** Returns null when the inputs form a valid range; otherwise an i18n key. */
function validatePair(fromInput: string, toInput: string): string | null {
  if (!fromInput || !toInput) return 'date_range_err_required';
  const from = new Date(`${fromInput}T00:00:00Z`);
  const to = new Date(`${toInput}T00:00:00Z`);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
    return 'date_range_err_invalid';
  }
  if (from > to) return 'date_range_err_order';
  const dayMs = 24 * 60 * 60 * 1000;
  const span = (to.getTime() - from.getTime()) / dayMs + 1;
  if (span > MAX_RANGE_DAYS) return 'date_range_err_too_long';
  return null;
}

export function DateRangePicker({
  presets,
  value,
  range,
  onChange,
  disableCustom = false,
}: DateRangePickerProps): JSX.Element {
  const { t } = useTranslation('default');
  const isCustom = value === 'custom';

  const [fromInput, setFromInput] = useState<string>(() => isoToDateInput(range.from));
  // For custom mode the picker stores end-date as the *inclusive* day. Subtract
  // one day from the exclusive RFC3339 `to` so the input shows what the user
  // picked, not the day after.
  const [toInput, setToInput] = useState<string>(() => {
    const d = new Date(range.to);
    d.setUTCDate(d.getUTCDate() - 1);
    return d.toISOString().slice(0, 10);
  });

  // Re-seed the inputs when the parent flips back to a preset so the picker
  // doesn't show a stale custom range on next open.
  const lastValue = useRef(value);
  useEffect(() => {
    if (lastValue.current !== value && value !== 'custom') {
      setFromInput(isoToDateInput(range.from));
      const d = new Date(range.to);
      d.setUTCDate(d.getUTCDate() - 1);
      setToInput(d.toISOString().slice(0, 10));
    }
    lastValue.current = value;
  }, [value, range.from, range.to]);

  const error = isCustom ? validatePair(fromInput, toInput) : null;

  const todayInput = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const handleApply = () => {
    if (error) return;
    onChange({
      key: 'custom',
      range: { from: dateInputToFromIso(fromInput), to: dateInputToToIso(toInput) },
    });
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div role="tablist" className="inline-flex bg-surface-2 border border-line rounded-md p-0.5">
        {presets.map((p) => (
          <button
            key={p.key}
            type="button"
            role="tab"
            aria-selected={value === p.key}
            onClick={() => onChange({ key: p.key, range: p.range })}
            className={cn(
              'px-3 py-1.5 text-[12px] font-medium rounded-sm transition-colors',
              value === p.key
                ? 'bg-surface text-text border border-line'
                : 'text-text-3 hover:text-text border border-transparent',
            )}
          >
            {p.label}
          </button>
        ))}
        {!disableCustom && (
          <button
            type="button"
            role="tab"
            aria-selected={isCustom}
            onClick={() =>
              onChange({
                key: 'custom',
                range: {
                  from: dateInputToFromIso(fromInput),
                  to: dateInputToToIso(toInput),
                },
              })
            }
            className={cn(
              'px-3 py-1.5 text-[12px] font-medium rounded-sm transition-colors',
              isCustom
                ? 'bg-surface text-text border border-line'
                : 'text-text-3 hover:text-text border border-transparent',
            )}
          >
            {t('date_range_custom')}
          </button>
        )}
      </div>

      {isCustom && (
        <div className="flex items-center gap-2 flex-wrap">
          <Input
            type="date"
            value={fromInput}
            onChange={(e) => setFromInput(e.target.value)}
            max={todayInput}
            className="h-9 w-[150px] text-[12px]"
            aria-label={t('date_range_from')}
          />
          <span className="text-text-3 text-[12px]">→</span>
          <Input
            type="date"
            value={toInput}
            onChange={(e) => setToInput(e.target.value)}
            max={todayInput}
            min={fromInput || undefined}
            error={!!error}
            className="h-9 w-[150px] text-[12px]"
            aria-label={t('date_range_to')}
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleApply}
            disabled={!!error}
          >
            {t('date_range_apply')}
          </Button>
          {error && (
            <span className="text-[12px] text-danger" role="alert">
              {t(error)}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
